import { EventPageVisibility, AccommodationPlaceRoomBlockStatus, AccommodationPlaceType, EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useWebsitePages } from '@shared/hooks/useWebsitePages/useWebsitePages';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { useGetEventCTAButtonsDataQuery } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.graphql.generated';
import { useWeddingCodeBannerBookableHotelQuery } from './WeddingCodeBannerBookableHotel.graphql.generated';
import type { WritableUtmParameters } from '@shared/utils/utmParameters';
import { applyUtmParametersToUrl, useUtmParameters } from '@shared/utils/utmParameters';
import { resolveInternalNavUtm, useRawUtmMedium } from '@apps/guest/packages/layout-engine/common/utils/internalNavUtm';

export type WeddingCodeBannerEligibilityArgs = Readonly<{
  eventId: string;
  eventHandle: string;
  /** `event.info.weddingCode` — the per-event code string (null until the event is published). */
  weddingCode?: string | null;
}>;

export type WeddingCodeBannerEligibility = Readonly<{
  isEligible: boolean;
  /** Guest-site path to the accommodations page the banner links to, carrying the banner's UTM. */
  accommodationsPath: string;
  /** The UTM stamped on `accommodationsPath` — pass it to the click telemetry so the two can't drift. */
  bannerUtm: WritableUtmParameters;
}>;

const WEDDING_CODE_BANNER_UTM_SOURCE = 'wedding_code_banner';
const WEDDING_CODE_BANNER_UTM_CAMPAIGN = 'wedding_code_banner_accommodations';

/**
 * Builds the banner's link, re-attaching the one param `applyUtmParametersToUrl` does not write:
 * the `utm_source_secondary` breadcrumb.
 *
 * Nav links keep it for free because `goToPath` forwards `location.search`. This path is rebuilt
 * from scratch, so without this the click telemetry would report a breadcrumb the page view lacks.
 */
const buildAccommodationsPath = (basePath: string, utm: WritableUtmParameters): string => {
  const path = applyUtmParametersToUrl(basePath, utm);
  if (!utm.utm_source_secondary) {
    return path;
  }

  // Parsed, not concatenated: appending after a `#fragment` would bury the param inside the
  // fragment, where the server never sees it. Today's base path has no hash — this keeps it safe
  // if one ever gains a section anchor.
  const parsed = new URL(path, 'https://relative.invalid');
  parsed.searchParams.set('utm_source_secondary', utm.utm_source_secondary);
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

/**
 * Per-guest eligibility for the wedding-code banner. The gate (from the PRD clarifications) is:
 *   1. a code exists (`weddingCode`, set on publish),
 *   2. the accommodations page is public,
 *   3. host hasn't turned the banner off (`travelSettings.showWeddingCodeBanner`),
 *   4. the viewing guest has RSVP'd **attending**, and
 *   5. the event has ≥1 non-cancelled hotel room block.
 *
 * The banner-owned minimal query (`WeddingCodeBannerBookableHotel`) returns BOTH the host toggle
 * (`travelSettings.showWeddingCodeBanner`) and the fields needed for `hasBookableHotel` in one travel
 * read — so the toggle can't gate its own query; the local checks (code present + accommodations public)
 * gate it instead. The RSVP check reuses `GetEventCTAButtonsData` (shared with the ActionBar).
 */
export const useWeddingCodeBannerEligibility = ({ eventId, eventHandle, weddingCode }: WeddingCodeBannerEligibilityArgs): WeddingCodeBannerEligibility => {
  const { getPageByName } = useWebsitePages();
  const accommodationsPage = getPageByName(EVENT_PAGE_SLUGS.Accommodations);

  // Inbound acquisition UTM always wins; the banner only fills slots the guest's URL left empty.
  const currentUtm = useUtmParameters();
  const rawUtmMedium = useRawUtmMedium();
  const bannerUtm = resolveInternalNavUtm(
    currentUtm,
    {
      utm_medium: 'web',
      utm_source: WEDDING_CODE_BANNER_UTM_SOURCE,
      utm_campaign: WEDDING_CODE_BANNER_UTM_CAMPAIGN,
    },
    rawUtmMedium,
  );
  const accommodationsPath = buildAccommodationsPath(`/${eventHandle}/${accommodationsPage?.pageSlug ?? EVENT_PAGE_SLUGS.Accommodations}`, bannerUtm);

  // Experiment gate: banner + checkout weddingCode injection show only for the 'treatment' variant
  // ('control' is the holdout; default 'control' so FE ships dark). Gating here covers both the
  // banner and the checkout injection (Accommodations reads `isEligible`), keeping them in lockstep.
  const isInTreatment = useFeatureValue('weddingCodeBannerExperiment').value === 'treatment';

  // Publish gate: only show once the event is PUBLISHED. Publish is when registerWeddingGroupCode runs
  // and the code actually exists in discount_coupon_service. Pre-publish, `weddingCode` is derived from
  // the couple's names but is NOT registered — so showing it (e.g. in the owner's draft preview) would
  // advertise a code that can't be used yet. Reuses the guest site's own draft-vs-published signal.
  const isEventPublished = useWebsitePublishCapabilityFeature().eventPublishedState.value;

  // Local gates — no network. The host toggle comes from the query below (can't gate its own fetch).
  const hasCode = Boolean(weddingCode);
  const accommodationsPublic = accommodationsPage?.visibility === EventPageVisibility.public;
  const localGate = hasCode && accommodationsPublic;

  // Skip only on gates stable at mount. Do not gate skip on `isInTreatment` or `isAttending` —
  // both resolve after hydration and toggling skip mid-load can leave the hook without data on refresh.
  const querySkip = !(isEventPublished && localGate);

  // Per-guest RSVP gate: only guests who RSVP'd **attending** see the banner. Reuses
  // GetEventCTAButtonsData (shared with the ActionBar), keyed to the viewing guest's activeSession.
  const { data: ctaData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventHandle },
    skip: querySkip,
    fetchPolicy: 'cache-and-network',
    batchMode: 'fast',
    // Keep this RSVP read off the SSR path (mirrors CtaButtons.controller): the server render can
    // cache the anonymous `activeSession`, and with cache-first that stale result would leave
    // `isAttending` false for an identified attending guest — hiding the banner. Fetch client-side.
    ssr: false,
  });
  const isAttending = ctaData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus === EventRSVPAttendanceStatus.attending;

  const { data } = useWeddingCodeBannerBookableHotelQuery({
    variables: { eventId },
    skip: querySkip,
    fetchPolicy: 'cache-and-network',
    batchMode: 'fast',
    // Banner is deferred content, not needed for first paint — keep it off the guest SSR path
    // (narrow-SSR-surface convention; see layout-engine/README.md).
    ssr: false,
  });

  // RSVP gate. Normally the banner shows only to a guest who has RSVP'd attending. But once the event
  // crosses the RSVP threshold (decided server-side, exposed as `shouldShowEventGroupCode`), we reveal
  // the code to ALL guests, not just those who RSVP'd. `shouldShowEventGroupCode` is fail-open (null on
  // upstream failure), so `=== true` keeps today's per-guest behavior whenever it can't be determined.
  const rsvpGateOpen = isAttending || data?.event?.shouldShowEventGroupCode === true;

  // Host opt-out toggle, from travel settings. Treat missing/null as OFF (require an explicit `true`):
  // travel_settings rows are sparse, so an event without a row — or a pre-feature NULL — must NOT show
  // the banner. New events default to `true` (DB default) once a travel_settings row exists.
  const bannerEnabled = data?.event?.travelSettings?.showWeddingCodeBanner === true;

  // "Bookable hotel through Joy (including availability)" — matches the guest "book on Joy" rule
  // (useAccommodationsCta): a catalog hotel that isn't just a custom link is bookable live through
  // Joy's booking engine, OR a Joy room block that still has availability (not cancelled).
  const hasBookableHotel = Boolean(
    data?.event?.accommodationPlacesV2?.some(place => {
      const isCatalogBookableHotel = place.type === AccommodationPlaceType.hotel && !place.isCustomLinkEnabled && place.isFromAccommodationCatalog;
      const hasActiveRoomBlock = Boolean(place.accommodationRoomBlock) && place.accommodationRoomBlock?.status !== AccommodationPlaceRoomBlockStatus.cancelled;
      return isCatalogBookableHotel || hasActiveRoomBlock;
    }),
  );

  return {
    // Eligible only when: in the experiment treatment, event published, code present + accommodations
    // public (localGate), RSVP gate open (guest RSVP'd attending OR event crossed the RSVP threshold),
    // host toggle on, and there's ≥1 Joy-bookable hotel.
    isEligible: isInTreatment && isEventPublished && localGate && rsvpGateOpen && bannerEnabled && hasBookableHotel,
    accommodationsPath,
    bannerUtm,
  };
};
